// LEGACY: Uses SCSS with CSS modules. New code should use @metarouter-private/mr-mui components instead.
// LEGACY: Uses @ion/components. New code should use @metarouter-private/mr-mui components instead.
/* eslint-disable filename-export/match-default-export */
/* disabling rule until refactor of analytics builder */

import React, { useEffect, useState } from 'react';
import pt from 'prop-types';

import { useUserContext } from '@ion/user';
import FeatureGate from '@ion/app/src/launch-darkly/gates/feature-gate';
import { MrH, AceCodeEditor, useScrollToTop, saveFile } from '@ion/components';
import CopyToClipboard from 'app/components/CopyToClipboard';
import { useCreateAJSFile, useTrackWithUser } from '@ion/api';
import permission from '@ion/api/Apollo/permissions';
import compressAndEncode from './compressAndEncode';
import {
  Box,
  Grid,
  Stack,
  Tooltip,
  Typography,
  Button,
  Download02Icon,
  CheckCircleIcon,
  UploadCloud01Icon,
  EyeIcon,
  EyeOffIcon,
  HelpCircleIcon,
} from '@metarouter-private/mr-mui';
import isFeatureEnabled from '../../launch-darkly/is-feature-enabled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { HOSTING_TYPES, READABLE_HOSTING_TYPE } from './analytics-builder-file-hosting';
import { useHistory, useLocation } from 'react-router-dom';

import s from './index.module.scss';
import BaseTable from './base-table/BaseTable';
import ConsentSourceResultRows from './ConsentSourceResultRows';
import ConsentCategoryRow from './SyncSettingsTable/ConsentCategoryRow';
import { FEATURE_FLAGS } from '../../launch-darkly/featureFlags';
import { INDEXED_DB_INT_VALUE, READABLE_STORAGE_TYPE } from './analytics-builder-advanced-settings';
import useIsGeneratedFileUpdated from './useIsGeneratedFileUpdated';
import buildMRHostedFileURL from './buildMRHostedFileUrl';
import { LoadingButton } from '@metarouter-private/mr-mui/dist/cjs/components';
import { notify } from '@ion/api';
import { SHARED_PARENT_GTAG_SYNCS } from './identity-sync-card/components/consts';
import SyncSettingsTable from './SyncSettingsTable';
import ANALYTICS_BUILDER_EVENTS from './analyticsBuilderEvents';
import useLogDeployment from 'api/deployments/useLogDeployment';

const SUCCESS_MESSAGE_BUILT = 'Success! Your file has been successfully built. 🎉';
const SUCCESS_MESSAGE_DEPLOYED = 'Success! Your file has been successfully deployed. 🎉';
const FAILURE_MESSAGE = 'Failed to upload file to cluster';
const REVIEW_SETTINGS_MESSAGE = 'Review settings before finalizing deployment';
const DOWNLOAD_PROMPT_MESSAGE =
  'Your Analytics.js file is ready for download. Please deploy it to your environment to make it live.';
const INVALIDATE_CACHE_MESSAGE =
  'To ensure your tracking file changes take effect immediately, invalidate your CDN cache after deployment. Without cache invalidation, changes may take up to 24 hours or more to propagate.';

const getInitialMainText = fileHostingType => {
  return fileHostingType === HOSTING_TYPES.metaRouter ? REVIEW_SETTINGS_MESSAGE : SUCCESS_MESSAGE_BUILT;
};

const getInitialSubText = fileHostingType => {
  return fileHostingType === HOSTING_TYPES.metaRouter ? INVALIDATE_CACHE_MESSAGE : DOWNLOAD_PROMPT_MESSAGE;
};

const AnalyticsBuilderResults = ({
  writekey,
  resultsData,
  analyticsSnippet,
  generatedFile,
  pipelineName,
  clusterName,
  isStandAloneSync,
  isUnminifyFileEnabled,
}) => {
  const history = useHistory();
  const {
    location: {
      state: { clusterId, analyticsFileId },
    },
  } = history;
  const featureFlags = useFlags();
  const crossDomainV1Enabled = isFeatureEnabled({ featureFlags, flagName: FEATURE_FLAGS.crossDomainV1 });

  const { pathname } = useLocation();
  const crossDomainResults = resultsData.syncInjectorSettings?.syncs.find(s => s.name === 'crossDomain');
  const crossDomainDirectResults = resultsData.syncInjectorSettings?.syncs.find(s => s.name === 'crossDomainDirect');
  useScrollToTop(pathname, 'AppLayout');
  const { user, currentOrg } = useUserContext();
  const userData = { email: user?.email, org: currentOrg?.display_name };
  const [disableUploadButton, setDisableUploadButton] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingSuccessful, setIsUploadingSuccessful] = useState(false);
  const [subText, setSubText] = useState(getInitialSubText(resultsData.fileHostingType));
  const [mainText, setMainText] = useState(getInitialMainText(resultsData.fileHostingType));
  const [showSnippet, setShowSnippet] = useState(false);

  const isMRHosted = resultsData.fileHostingType === HOSTING_TYPES.metaRouter;
  const fileLocation = isMRHosted ? buildMRHostedFileURL(resultsData.cdnURL, writekey) : null;
  // new URL() throws on an empty host, and standalone files have none; the endpoint is only shown for AJS files.
  const clusterFileEndpoint = isMRHosted && !isStandAloneSync ? buildMRHostedFileURL(resultsData.host, writekey) : null;

  const [trackUploadSuccess] = useTrackWithUser(ANALYTICS_BUILDER_EVENTS.ANALYTICS_FILE_UPLOAD_COMPLETE);
  const [trackDownloadSuccess] = useTrackWithUser(ANALYTICS_BUILDER_EVENTS.ANALYTICS_FILE_DOWNLOADED);

  const { isUpdated: isGeneratedFileUpdated, error: isGeneratedFileUpdatedError } = useIsGeneratedFileUpdated({
    generatedFile,
    remoteFileLocation: fileLocation,
    hostingType: resultsData.fileHostingType,
  });

  const syncInjectorDataFiltered = resultsData.syncInjectorSettings?.syncs.filter(s => s.name !== 'crossDomain');

  const handleDownloadClick = () => {
    saveFile({
      compressFile: resultsData.compressFile,
      mimeType: 'text/javascript',
      content: generatedFile,
      filename: resultsData.compressFile ? `${writekey}.js.gz` : `${writekey}.js`,
      compressionType: 'gzip',
    });

    trackDownloadSuccess({ pipelineName });
  };

  const handleUploadClick = async () => {
    if (isUploadingSuccessful) return;
    setIsUploading(true);
    setMainText(`Uploading to ${resultsData.host || clusterName}`);
    setSubText('Deploying your file to the cluster...');

    const startTime = Date.now();
    const MIN_DURATION = 3000;

    try {
      const response = await createAJSFIle({
        clusterId: clusterId,
        writekey: writekey,
        gzippedFileContent: compressAndEncode({ input: generatedFile }),
        email: userData?.email || 'UI user',
      });

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_DURATION - elapsedTime);

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      handleUploadResponse(response);
    } catch (error) {
      console.log('Error uploading file to cluster', error);
      handleUploadError();
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadResponse = response => {
    if (response.errors) {
      handleUploadError();
    } else {
      logDeployment({ writeKey: resultsData.writeKey, analyticsFileId }).catch(error => {
        console.error('Failed to log deployment:', error);
      });
      setIsUploadingSuccessful(true);
      trackUploadSuccess({ pipelineName });
      setMainText(SUCCESS_MESSAGE_DEPLOYED);
      setSubText(`Your Analytics.js file is now live and hosted on your Cluster.`);
      notify.success({
        message: `Analytics.js for ${pipelineName} has been successfully deployed to ${clusterName}`,
      });
    }
  };

  const handleSnowShippetClick = () => {
    setShowSnippet(!showSnippet);
  };

  const handleCopySnippetClick = () => {
    navigator.clipboard.writeText(analyticsSnippet);
  };

  const handleUploadError = () => {
    setMainText(FAILURE_MESSAGE);
    setSubText('Please try again or contact support if the issue persists.');
    setIsUploading(false);
  };

  const [createAJSFIle] = useCreateAJSFile({ permission: permission.createAnalyticsJSFile });
  const [logDeployment] = useLogDeployment();

  useEffect(() => {
    // if new generated file contents change from MR hosted file, enable upload
    setDisableUploadButton(!isGeneratedFileUpdated);

    // if comparison errors out, continue to allow upload
    if (isGeneratedFileUpdatedError) {
      setDisableUploadButton(false);
    }
  }, [isGeneratedFileUpdated, isGeneratedFileUpdatedError]);

  return (
    <>
      <Box sx={{ mt: 3, p: 3 }} className={s.buttonContainer}>
        <Grid container direction="row" wrap="nowrap" alignItems="center" spacing={8}>
          <Grid item>
            <Box display="flex" justifyContent="flex-start" alignItems="center">
              <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ gap: 1 }}>
                <Typography variant="h6">{mainText}</Typography>
                <Typography variant="body2" className={isMRHosted ? s.subTextMinHeight : undefined}>
                  {subText}
                </Typography>
                {isMRHosted && !isStandAloneSync && (
                  <Stack spacing={1} className={s.breakAllText}>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          Distribution URL
                        </Typography>
                        <Tooltip
                          title="The URL used by client devices to fetch this file, typically served through a CDN."
                          arrow
                          placement="top"
                        >
                          <span>
                            <HelpCircleIcon fontSize="small" color="secondary" />
                          </span>
                        </Tooltip>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {resultsData.cdnURL}
                        </Typography>
                        <CopyToClipboard text={resultsData.cdnURL} />
                      </Stack>
                    </Stack>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          Cluster File Endpoint
                        </Typography>
                        <Tooltip
                          title="The URL where the latest version of your file can be retrieved. Use this endpoint to automate file fetching via a CDN, CI/CD pipeline, or other tooling."
                          arrow
                          placement="top"
                        >
                          <span>
                            <HelpCircleIcon fontSize="small" color="secondary" />
                          </span>
                        </Tooltip>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {clusterFileEndpoint}
                        </Typography>
                        <CopyToClipboard text={clusterFileEndpoint} />
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs>
            <Grid direction="row" justifyContent="flex-end" alignItems="center" container wrap="nowrap" spacing={1}>
              <Grid item>
                <Button
                  sx={{
                    px: isMRHosted ? 2 : 4,
                  }}
                  variant={isMRHosted ? 'outlined' : 'contained'}
                  color={isMRHosted ? 'secondary' : 'primary'}
                  size="large"
                  onClick={handleDownloadClick}
                  startIcon={<Download02Icon color={isMRHosted ? 'action' : 'main'} />}
                >
                  Download
                </Button>
              </Grid>
              {isMRHosted && (
                <Grid item>
                  <LoadingButton
                    disabled={disableUploadButton}
                    loading={isUploading}
                    loadingPosition="start"
                    sx={{
                      px: 2,
                      whiteSpace: 'nowrap',
                      minWidth: '178px',
                      minHeight: '42px',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                    variant="contained"
                    color={isUploadingSuccessful ? 'success' : 'primary'}
                    size="large"
                    onClick={handleUploadClick}
                    startIcon={
                      isUploadingSuccessful ? <CheckCircleIcon color="main" /> : <UploadCloud01Icon color="main" />
                    }
                  >
                    {isUploadingSuccessful ? 'Deployed' : 'Upload to Cluster'}
                  </LoadingButton>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <Box
        className={s.codeSnippetSection}
        sx={{
          mt: 2,
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6">Code Snippet</Typography>
        <Grid
          container
          direction="row"
          wrap="nowrap"
          alignItems="center"
          sx={{
            gap: 10,
          }}
        >
          <Grid item xs>
            <Typography sx={{ mt: 1 }} variant="body2">
              The MetaRouter tag should be added near the top of the {'<head>'} tag and before any other script or CSS
              tags.
            </Typography>
          </Grid>
          <Grid item>
            <Grid container direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
              <Grid item>
                <Button
                  sx={{
                    height: '36px',
                  }}
                  variant="text"
                  color="secondary"
                  size="medium"
                  startIcon={showSnippet ? <EyeOffIcon /> : <EyeIcon />}
                  onClick={handleSnowShippetClick}
                >
                  {showSnippet ? 'Hide' : 'Show'} Snippet
                </Button>
              </Grid>
              <Grid item>
                <Button
                  sx={{
                    height: '36px',
                  }}
                  variant="outlined"
                  color="secondary"
                  size="medium"
                  onClick={handleCopySnippetClick}
                >
                  Copy to Clipboard
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {showSnippet && (
          <pre>
            <AceCodeEditor
              className="read-only"
              lang="lua"
              name="analyticsSnippet"
              value={analyticsSnippet}
              readOnly={true}
              testId="analyticsSnippet"
            />
          </pre>
        )}
      </Box>

      <section className={s.section}>
        <MrH h="h5" sans styleNames="sans noMargin">
          <div className={s.circle}>1</div> File location and settings
        </MrH>
        <div className={s.subSection}>
          <BaseTable>
            <tbody>
              {resultsData.fileHostingType && (
                <tr>
                  <td>Hosting type</td>
                  <td>{READABLE_HOSTING_TYPE[resultsData.fileHostingType]}</td>
                </tr>
              )}
              <tr>
                <td>{isStandAloneSync || isMRHosted ? 'Distribution URL' : 'Analytics.js Hosting URL'}</td>
                <td>{resultsData.cdnURL}</td>
              </tr>
              {isMRHosted && !isStandAloneSync && (
                <tr>
                  <td>Cluster File Endpoint</td>
                  <td>{clusterFileEndpoint}</td>
                </tr>
              )}
              {!isStandAloneSync && (
                <tr>
                  <td>Ingestion URL</td>
                  <td>{resultsData.host}</td>
                </tr>
              )}
              <tr>
                <td>Writekey</td>
                <td>{resultsData.writeKey}</td>
              </tr>
              <tr>
                <td>Compress File</td>
                <td>{resultsData.compressFile ? 'yes' : 'no'}</td>
              </tr>
              {isUnminifyFileEnabled && (
                <tr>
                  <td>Unminify File</td>
                  <td>{resultsData.minifyFile === false ? 'yes' : 'no'}</td>
                </tr>
              )}
              {!isStandAloneSync && (
                <tr>
                  <td>Use HTTP cookie</td>
                  <td>{resultsData.syncInjectorSettings?.itpSettings?.enabled ? 'yes' : 'no'}</td>
                </tr>
              )}
              <FeatureGate flagName={FEATURE_FLAGS.builderServerAdvancedSettings}>
                <tr>
                  <td className={s.setting}>Namespace</td>
                  <td className={s.value}>{resultsData?.windowVariableName}</td>
                </tr>
                <tr>
                  <td className={s.setting}>Storage Prefix</td>
                  <td className={s.value}>{resultsData?.syncInjectorSettings?.storage?.marker}</td>
                </tr>
                <tr>
                  <td className={s.setting}>Storage Type</td>
                  <td className={s.value}>{READABLE_STORAGE_TYPE[resultsData?.syncInjectorSettings?.storage?.type]}</td>
                </tr>
                {resultsData?.syncInjectorSettings?.storage?.type === INDEXED_DB_INT_VALUE && (
                  <tr>
                    <td className={s.setting}>Storage Database</td>
                    <td className={s.value}>{resultsData?.syncInjectorSettings?.storage?.database}</td>
                  </tr>
                )}
              </FeatureGate>
            </tbody>
          </BaseTable>
        </div>
      </section>

      <section className={s.section}>
        <MrH h="h5" sans styleNames="sans noMargin">
          <div className={s.circle}>2</div> Compliance Settings
        </MrH>
        <div className={s.subSection}>
          <BaseTable>
            <tbody>
              <tr>
                <td>Compliance Setting</td>
                <td>
                  {resultsData.syncInjectorSettings?.privacy?.impliedConsent
                    ? 'Implied consent (opt-in by default)'
                    : 'Explicit consent (opt-out by default)'}
                </td>
              </tr>
              <ConsentSourceResultRows
                consent={resultsData.syncInjectorSettings?.consent}
                privacyCookieName={resultsData.syncInjectorSettings?.privacy?.cookieName}
              />
              {resultsData.syncInjectorSettings?.consent?.matchIDConsentCode !== undefined && (
                <tr>
                  <td>anonymousId Consent Code</td>
                  <td>{resultsData.syncInjectorSettings?.consent?.matchIDConsentCode}</td>
                </tr>
              )}
            </tbody>
          </BaseTable>
        </div>
      </section>

      <section className={s.section}>
        <MrH h="h5" sans styleNames="sans noMargin">
          <div className={s.circle}>3</div> Identity Syncs
        </MrH>
        <div className={s.subSection}>
          {syncInjectorDataFiltered.map((sync, index) => {
            if (SHARED_PARENT_GTAG_SYNCS.has(sync.name)) {
              return null; // Skip rendering individual syncs that are part of SHARED_PARENT_GTAG_SYNCS
            }
            return <SyncSettingsTable key={index} sync={sync} />;
          })}
          {syncInjectorDataFiltered.some(sync => SHARED_PARENT_GTAG_SYNCS.has(sync.name)) && (
            <div>
              <MrH h="h6" sans styleNames="sans noMargin">
                Shared Google Tag Settings
              </MrH>
              <BaseTable>
                <tbody>
                  <tr>
                    <td>tagID</td>
                    <td>{resultsData.syncInjectorSettings.googleGtagSettings.tagID}</td>
                  </tr>
                  <tr>
                    <td>loadGtag</td>
                    <td>{resultsData.syncInjectorSettings.googleGtagSettings.loadGtag ? 'true' : 'false'}</td>
                  </tr>
                  <tr>
                    <td>loadWhenConsentDenied</td>
                    <td>
                      {resultsData.syncInjectorSettings.googleGtagSettings.consent.loadWhenConsentDenied
                        ? 'true'
                        : 'false'}
                    </td>
                  </tr>
                </tbody>
              </BaseTable>

              {syncInjectorDataFiltered
                .filter(sync => SHARED_PARENT_GTAG_SYNCS.has(sync.name))
                .map((sync, index) => (
                  <SyncSettingsTable key={index} sync={sync} />
                ))}
            </div>
          )}
        </div>
      </section>

      <FeatureGate flagName="ecmAjsCrossDomainIdentitySync">
        <section className={s.section}>
          <MrH h="h5" sans styleNames="sans noMargin">
            <div className={s.circle}>4</div> Cross-Domain Identity Sync
          </MrH>
          <div className={s.subSection}>
            <div key={'crossDomain'}>
              <BaseTable testId="crossDomainResults">
                <tbody>
                  {crossDomainResults && (
                    <>
                      <ConsentCategoryRow consentCategoryId={crossDomainResults?.consentCategoryId} />
                      <tr>
                        <td className={s.setting}>Urls</td>
                        <td className={s.value}>{crossDomainResults?.settings?.syncURLs.join(', ')}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </BaseTable>
            </div>
          </div>
        </section>
      </FeatureGate>
      <FeatureGate flagName="crossDomainDirect">
        <section className={s.section}>
          <MrH h="h5" sans styleNames="sans noMargin">
            <div className={s.circle}>{crossDomainV1Enabled ? 5 : 4}</div> Cross-Domain-Direct Identity Sync
          </MrH>
          <div className={s.subSection}>
            <div key={'crossDomainDirect'}>
              <BaseTable testId="crossDomainDirectResults">
                <tbody>
                  {crossDomainDirectResults && (
                    <>
                      <ConsentCategoryRow consentCategoryId={crossDomainDirectResults?.consentCategoryId} />
                      <tr>
                        <td className={s.setting}>Cluster URL (A record)</td>
                        <td className={s.value}>{crossDomainDirectResults?.settings?.syncURL}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </BaseTable>
            </div>
          </div>
        </section>
      </FeatureGate>
      {!isStandAloneSync && (
        <FeatureGate flagName="ecmAjsClientSideIntegrations">
          <section className={s.section}>
            <MrH h="h5" sans styleNames="sans noMargin">
              <div className={s.circle}>&#65290;</div> Client-Side Integrations
            </MrH>
            <div className={s.subSection}>
              {resultsData.integrations?.map((integration, index) => (
                <div key={index}>
                  <MrH h="h6" sans styleNames="sans noMargin">
                    {integration.friendlyName}
                  </MrH>
                  <BaseTable testId="clientSideIntegrationsResults">
                    <tbody>
                      {Object.entries(integration.options).map(entry => (
                        // setting = entry[0], value = entry[1]
                        <tr key={entry[0]}>
                          <td className={s.setting}>{entry[0]}</td>
                          <td className={s.value}>
                            {typeof entry[1] === 'string' ? entry[1] : JSON.stringify(entry[1])}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </BaseTable>
                </div>
              ))}
            </div>
          </section>
        </FeatureGate>
      )}
    </>
  );
};

AnalyticsBuilderResults.propTypes = {
  writekey: pt.string,
  resultsData: pt.object,
  analyticsSnippet: pt.string,
  generatedFile: pt.string,
  compressFile: pt.bool,
  pipelineName: pt.string,
  clusterName: pt.string,
  isStandAloneSync: pt.bool,
  isUnminifyFileEnabled: pt.bool,
};

export default AnalyticsBuilderResults;
